"use client";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { Analytics } from "../../Lib/Analytics/events";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { useLocale } from "../Context/LocaleContext";

/**
 * Keeps GA4's global params (user_status, language) in step with the session
 * and reports App Router navigations as page_view. Renders nothing.
 *
 * page_view waits for the auth check, so the first one of a load carries the
 * real user_status instead of the anonymous default. Calls made before GA4
 * has consent are queued by analytics() and replay in order.
 */
export default function GaContextTracker() {
  const pathname = usePathname();
  const { locale } = useLocale();
  const [isAuthCheckComplete] = useRecState<boolean>("isAuthCheckCompleteStateAtom");
  const [isAuthenticated] = useRecState<boolean>("isAuthenticatedStateAtom");
  const [isAnonymous] = useRecState<boolean>("isAnonymousStateAtom");
  const userStatus = isAuthenticated && !isAnonymous ? "logged_in" : "guest";

  // Declared before the page_view effect: effects run in order, so the
  // context is set before the event that should carry it.
  useEffect(() => {
    if (!isAuthCheckComplete) return;
    Analytics.setContext({ userStatus, language: locale });
  }, [isAuthCheckComplete, userStatus, locale]);

  useEffect(() => {
    if (!isAuthCheckComplete || !pathname) return;
    Analytics.pageView(pathname);
  }, [isAuthCheckComplete, pathname]);

  return null;
}
